import {
  ActiveOrdersContext,
  ActiveOrdersContextType,
} from '@/contexts/activeOrders/useActiveOrdersContext'
import useOrdersWebSocket from '@/contexts/ordersWebSocket/useOrdersWebSocket'
import {
  DEFAULT_SELLER_ACTIVE_ORDERS_VARIABLES,
  useActiveOrders,
} from '@/proxy/order/hooks'
import type { SellerActiveOrdersVariables } from '@/proxy/order/hooks/useActiveOrders/types'
import { Routes } from '@/resources/enums/enumRoutes'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import { ActiveOrder } from '@/resources/types'
import {
  ACTIVE_ORDERS_SEARCH_DEBOUNCE_MS,
  hasKanbanFiltersAppliedToQuery,
  toActiveOrdersSearchParam,
} from '@/resources/utils/orders/activeOrdersKanbanFilter'
import { logger } from '@/monitoring/logger'
import { CHECK_ORDERS_POLLING_TIME } from '@/seller.application.polling.time.config'
import useFeatureToggle from '@/hooks/useFeatureToggle'
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

const getGraphqlRequestHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined
  }
  const response = (error as { response?: { status?: number } }).response
  return response?.status
}

const ActiveOrdersProvider = ({ children }: PropsWithChildren) => {
  const isSellerKanbanFilterEnabled =
    useFeatureToggle({ splitName: 'seller_kanban_filter' }) === FeatureToggle.ON

  const [kanbanFilterSearchInput, setKanbanFilterSearchInput] = useState('')
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('')
  const [kanbanFilterDeliveryType, setKanbanFilterDeliveryType] = useState<
    number | null
  >(null)

  const { orders, isActive, hasUpdatedOrders } = useOrdersWebSocket()
  const prevIsActive = useRef<boolean>(false)
  const prevWssFeedsOrderListRef = useRef(false)
  const { pathname } = useLocation()
  const isActiveOrdersRoute =
    pathname === (Routes.ORDERS as string) ||
    pathname === (Routes.ORDERS_MAP as string)
  const isNanoDropRoute = pathname === (Routes.NANO_DROP as string)

  useEffect(() => {
    const onOrdersSection =
      pathname === (Routes.ORDERS as string) ||
      pathname === (Routes.ORDERS_MAP as string) ||
      pathname === (Routes.NANO_DROP as string)
    if (!onOrdersSection) {
      setKanbanFilterSearchInput('')
      setDebouncedSearchInput('')
      setKanbanFilterDeliveryType(null)
    }
  }, [pathname])

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearchInput(kanbanFilterSearchInput)
    }, ACTIVE_ORDERS_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [kanbanFilterSearchInput])

  const sellerActiveOrdersVariables: SellerActiveOrdersVariables =
    useMemo(() => {
      if (!isSellerKanbanFilterEnabled) {
        return DEFAULT_SELLER_ACTIVE_ORDERS_VARIABLES
      }
      return {
        search: toActiveOrdersSearchParam(debouncedSearchInput),
        deliveryType: kanbanFilterDeliveryType,
      }
    }, [
      isSellerKanbanFilterEnabled,
      debouncedSearchInput,
      kanbanFilterDeliveryType,
    ])

  const isKanbanFilterSearchDebouncing =
    isSellerKanbanFilterEnabled &&
    kanbanFilterSearchInput !== debouncedSearchInput

  const kanbanFiltersAppliedToQuery = useMemo(
    () =>
      hasKanbanFiltersAppliedToQuery(
        isSellerKanbanFilterEnabled,
        debouncedSearchInput,
        kanbanFilterDeliveryType
      ),
    [
      debouncedSearchInput,
      isSellerKanbanFilterEnabled,
      kanbanFilterDeliveryType,
    ]
  )

  const isWebSocketEnabled =
    useFeatureToggle({ splitName: 'seller_enable_wss' }) === FeatureToggle.ON

  const wssFeedsOrderList =
    isWebSocketEnabled &&
    isActive &&
    hasUpdatedOrders &&
    !kanbanFiltersAppliedToQuery

  useEffect(() => {
    const wasWssFeeding = prevWssFeedsOrderListRef.current
    prevWssFeedsOrderListRef.current = wssFeedsOrderList

    if (
      wasWssFeeding &&
      !wssFeedsOrderList &&
      isWebSocketEnabled &&
      !kanbanFiltersAppliedToQuery
    ) {
      logger.info('active_orders_wss_fallback_to_polling', {
        event: 'active_orders_wss_fallback_to_polling',
      })
    }
  }, [wssFeedsOrderList, isWebSocketEnabled, kanbanFiltersAppliedToQuery])

  const graphqlPollingPausedBySocket = wssFeedsOrderList

  const isQueryDisabled = wssFeedsOrderList
  const shouldFetchActiveOrders = !isNanoDropRoute && !isQueryDisabled

  const {
    isFetching: isQueryFetching,
    isFetched: isQueryFetched,
    data: { activeOrders: queryOrders = [], error: queryError } = {},
    error: queryRequestError,
    isError: isQueryRequestError,
    refetch,
  } = useActiveOrders(
    {
      gcTime: 0,
      refetchInterval: graphqlPollingPausedBySocket
        ? false
        : CHECK_ORDERS_POLLING_TIME,
      refetchIntervalInBackground: true,
      enabled: shouldFetchActiveOrders,
    },
    !isActiveOrdersRoute && !isNanoDropRoute,
    sellerActiveOrdersVariables
  )

  useEffect(() => {
    if (!isActive && isActive !== prevIsActive.current) void refetch()
    prevIsActive.current = isActive
  }, [isActive, refetch])

  const activeOrders: ActiveOrder[] = useMemo(() => {
    if (kanbanFiltersAppliedToQuery) {
      return queryOrders
    }
    if (wssFeedsOrderList) {
      logger.info('[WebSocket] Returning orders from WebSocket.')
      return orders
    } else {
      logger.info('[WebSocket] Returning orders from Polling.')
      return queryOrders
    }
  }, [kanbanFiltersAppliedToQuery, wssFeedsOrderList, orders, queryOrders])

  const httpStatus = getGraphqlRequestHttpStatus(queryRequestError)
  const isActiveOrdersQueryError =
    isQueryRequestError && (httpStatus === 500 || httpStatus === 503)

  const context: ActiveOrdersContextType = useMemo(
    () => ({
      activeOrders,
      isPollingActive: !graphqlPollingPausedBySocket,
      isFromWss: wssFeedsOrderList,
      isQueryFetching,
      isFetched: kanbanFiltersAppliedToQuery
        ? isQueryFetched
        : isQueryFetched || (isWebSocketEnabled && hasUpdatedOrders),
      queryError,
      isActiveOrdersQueryError,
      isSellerKanbanFilterEnabled,
      kanbanFiltersAppliedToQuery,
      kanbanFilterSearchInput,
      setKanbanFilterSearchInput,
      kanbanFilterDeliveryType,
      setKanbanFilterDeliveryType,
      isKanbanFilterSearchDebouncing,
    }),
    [
      activeOrders,
      graphqlPollingPausedBySocket,
      wssFeedsOrderList,
      isQueryFetching,
      queryError,
      isQueryFetched,
      isActiveOrdersQueryError,
      isSellerKanbanFilterEnabled,
      kanbanFiltersAppliedToQuery,
      isWebSocketEnabled,
      hasUpdatedOrders,
      kanbanFilterSearchInput,
      kanbanFilterDeliveryType,
      isKanbanFilterSearchDebouncing,
    ]
  )

  return (
    <ActiveOrdersContext.Provider value={context}>
      {children}
    </ActiveOrdersContext.Provider>
  )
}

export default ActiveOrdersProvider
